import { clamp, Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import { PositionType } from '../../../lib/defs/types';
import {
  despawnBlockEntity,
  DogBlockEntity,
  moveBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapCellBaseOpen } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { BreakEffect } from './BreakEffect';
import { DogBlockView } from '../../views/blocks/DogBlockView';

// types
//-----------------------------------------------------------------------------
// public
export type DogEffectOptions = {
  subject: DogBlockEntity;
};

// private
type FindResult = {
  empty?: boolean;
  position?: Vector2;
};

/*
    moves the dog in the given direction if theres any room
*/
export class DogEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: DogEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: DogEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const dog = this._options.subject;
    const oldViewPosition = { x: dog.c.view2d.view.x, y: dog.c.view2d.view.y };

    // get to position for dog to move to
    const result = this._findToPosition();
    if (!result) return false;

    // if position found
    if (result.position) {
      const phase = this._scene.sessionEntity.c.phase;

      // push activations
      phase.activePush();

      // move dog to this cell
      moveBlockEntity(this._scene, dog, result.position);

      // Get position again after move
      const newPosition = this._findToPosition();
      if (!newPosition) return false;

      // Check new position to see if dog reached the end of the map
      const shouldDespawnAfterwards = !!newPosition.empty;

      // animate move
      await this._animateMove(dog, oldViewPosition);

      // pop activations
      if (phase.activations > 0) {
        await phase.activePop();
      }

      // If the feature flag is enabled, despawn the dog if it reached the end, regardless of activations
      if (shouldDespawnAfterwards) this.despawn(dog);
    } else if (result.empty) {
      // Dog has reached the end of the map
      this.despawn(dog);
    } else return false;

    return true;
  }

  private despawn(dog: DogBlockEntity) {
    if (dog.scene) {
      new BreakEffect(this._scene, {
        position: dog.c.position.mapPosition,
        size: dog.c.block.props,
        color: 0xf1ca6c,
      }).execute();
    }

    // despawn dog
    despawnBlockEntity(this._scene, dog);
  }

  // private: animations
  //-------------------------------------------------------------------------
  private async _animateMove(dog: DogBlockEntity, fromPosition: PositionType) {
    const dogView = dog.c.view2d.view.children[0] as DogBlockView;
    const distance = Math.abs(dog.c.view2d.view.x - fromPosition.x);
    const duration = clamp(distance * 0.005, 0.2, 0.6);

    // animate to requested view position
    await gsap.from(dog.c.view2d.view, {
      x: fromPosition.x,
      y: fromPosition.y,
      duration,
      ease: 'sine.out',
      onUpdate: () => {
        dogView.frame += 0.1;
      },
    });

    // eslint-disable-next-line require-atomic-updates
    dogView.frame = 0;
  }

  // private: support
  //-------------------------------------------------------------------------
  private _findToPosition(): FindResult | undefined {
    const dog = this._options.subject;
    const offset = dog.c.blockDog.view.direction === 'right' ? 1 : -1;
    const position = dog.c.position.mapPosition.clone();
    const last: FindResult = {};

    // find last open cell/position
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Break it if somehow the scene got disposed
      if (!this._scene?.sessionEntity) {
        return undefined;
      }

      // shift position
      position.x += offset;

      // get map cell for this position
      const cell = this._scene.sessionEntity.c.map.getCellAt(position);

      // end if empty cell
      if (!cell?.enabled) {
        last.empty = true;
        break;
      }

      // end if cell occupied
      if (!mapCellBaseOpen(cell)) break;

      // update last
      last.position = position.clone();
    }

    return last;
  }
}
